import React, { useState, useMemo } from 'react';
import { Modal, Button, Alert, Typography, Steps } from 'antd';
import { BankOutlined, UploadOutlined, SendOutlined, CheckCircleOutlined, CopyOutlined } from '@ant-design/icons';
import FileUpload from '../../../components/FileUpload';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const BankTransferModal = ({ 
  open, 
  onCancel, 
  onConfirm, 
  loading = false,
  merchantDetails,
  total,
  currency = "NGN"
}) => {
  const [uploadedReceipt, setUploadedReceipt] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  // Generate a stable order ID upfront so the upload can reference it
  const generatedOrderID = useMemo(
    () => `ud_bt${Math.floor(Math.random() * 10000000000)}`,
    [open]
  );

  // Extract bank details from merchant details
  const bankDetails = {
    bank_name: merchantDetails?.bank_name || merchantDetails?.merchant_details?.bank_name,
    account_name: merchantDetails?.bank_account_name || merchantDetails?.merchant_details?.bank_account_name,
    account_number: merchantDetails?.bank_account_number || merchantDetails?.merchant_details?.bank_account_number
  };

  const handleReceiptUpload = (fileData) => {
    console.log('BankTransferModal: handleReceiptUpload called with:', fileData);
    setUploadedReceipt(fileData);
    if (fileData) {
      setCurrentStep(1);
    } else {
      setCurrentStep(0);
    }
  };

  const handleConfirmOrder = () => {
    console.log('BankTransferModal: handleConfirmOrder called');
    console.log('BankTransferModal: uploadedReceipt:', uploadedReceipt);
    if (uploadedReceipt && onConfirm) {
      onConfirm({ ...uploadedReceipt, orderID: generatedOrderID });
    }
  };

  const handleModalCancel = () => {
    setCurrentStep(0);
    setUploadedReceipt(null);
    onCancel();
  };

  const steps = [
    {
      title: 'Upload Receipt',
      icon: <UploadOutlined />,
    },
    {
      title: 'Send Order',
      icon: <SendOutlined />,
    },
    {
      title: 'Verification',
      icon: <CheckCircleOutlined />,
    }
  ];

  return (
    <Modal
      open={open}
      onCancel={handleModalCancel}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BankOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
          <span>Bank Transfer Payment</span>
        </div>
      }
      width={600}
      footer={null}
      destroyOnClose
    >
      <div style={{ padding: '20px 0' }}>
        {/* Progress Steps */}
        <Steps current={currentStep} style={{ marginBottom: '32px' }}>
          {steps.map((step, index) => (
            <Step 
              key={index}
              title={step.title} 
              icon={step.icon}
            />
          ))}
        </Steps>

        {/* Bank Transfer Details */}
        <div style={{ 
          background: '#f8f9fa', 
          padding: '20px', 
          borderRadius: '8px',
          marginBottom: '24px',
          border: '1px solid #e9ecef'
        }}>
          <Paragraph style={{ margin: '0 0 16px 0', fontWeight: 'bold', fontSize: '16px' }}>
            Transfer {currency} {total?.toFixed(2)} to:
          </Paragraph>
          {bankDetails?.bank_name ? (
            <div style={{ display: 'grid', gap: '8px' }}>
              <div>
                <Text strong>Bank:</Text> <Text copyable>{bankDetails.bank_name}</Text>
              </div>
              <div>
                <Text strong>Account Name:</Text> <Text copyable={{ icon: <CopyOutlined style={{ color: '#1890ff' }} /> }}>{bankDetails.account_name}</Text>
              </div>
              <div>
                <Text strong>Account Number:</Text> <Text copyable={{ icon: <CopyOutlined style={{ color: '#1890ff' }} /> }}>{bankDetails.account_number}</Text>
              </div>
            </div>
          ) : (
            <Alert
              message="Bank details not available"
              description="Please contact the merchant for bank transfer details."
              type="warning"
              showIcon
            />
          )}
        </div>

        {currentStep === 0 && (
          <div>
            <FileUpload 
              onFileUpload={handleReceiptUpload}
              disabled={loading}
            />
          </div>
        )}

        {currentStep === 1 && uploadedReceipt && (
          <Alert
            message="Receipt uploaded successfully!"
            description="Your order will be verified within 2-24 hours. You'll receive confirmation once approved."
            type="success"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginTop: '32px',
          paddingTop: '20px',
          borderTop: '1px solid #f0f0f0'
        }}>
          <Button onClick={handleModalCancel} size="large">
            Cancel
          </Button>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            {currentStep === 1 && uploadedReceipt && (
              <Button 
                onClick={() => setCurrentStep(0)}
                size="large"
              >
                Change Receipt
              </Button>
            )}
            
            <Button
              type="primary"
              onClick={handleConfirmOrder}
              disabled={!uploadedReceipt || currentStep !== 1}
              loading={loading}
              size="large"
              icon={<SendOutlined />}
            >
              Send Order
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BankTransferModal;
